import { useQuery, UseQueryResult } from '@tanstack/react-query';
import config from 'src/config';
import ApiResponse from 'src/api/ApiResponse';
import ApiError from 'src/api/ApiError';
import { CompanyDTO } from './useCompany';

export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor?: string | null;
  endCursor?: string | null;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface PaginatedApiResponse {
  companies: CompanyDTO[];
  pageInfo: PageInfo;
}

export interface QueryParams {
  first?: number;
  after?: string;
  offset?: number;
  search?: string;
  modules?: string[];
  supplierTagIds?: string[];
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
}

const usePaginatedCompanies = (
  params?: QueryParams,
  enabled = true,
): UseQueryResult<PaginatedApiResponse, ApiError> => {
  return useQuery<PaginatedApiResponse, ApiError>({
    queryKey: ['companies', 'paginated', params],
    queryFn: async (): Promise<PaginatedApiResponse> => {
      const queryParams = new URLSearchParams();

      if (params?.first) {
        queryParams.append('first', params.first.toString());
      }

      if (params?.after) {
        queryParams.append('after', params.after);
      }

      if (params?.offset !== undefined) {
        queryParams.append('offset', params.offset.toString());
      }

      if (params?.search) {
        queryParams.append('search', params.search);
      }

      if (params?.modules && params.modules.length > 0) {
        queryParams.append('modules', params.modules.join(','));
      }

      if (params?.supplierTagIds && params.supplierTagIds.length > 0) {
        queryParams.append('supplierTagIds', params.supplierTagIds.join(','));
      }

      if (params?.sortBy) {
        queryParams.append('sortBy', params.sortBy);
      }

      if (params?.sortDirection) {
        queryParams.append('sortDirection', params.sortDirection);
      }

      const url = `${
        config.apiBaseUrl
      }/core/companies?${queryParams.toString()}`;

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorResponse: ApiError = await response.json();
        throw new Error(errorResponse.message);
      }

      const result: ApiResponse<PaginatedApiResponse> = await response.json();
      return result.data;
    },
    enabled,
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
};

export default usePaginatedCompanies;
